/**
 * The one place the app talks to the network.
 *
 * Service modules call `api.get(...)` / `api.post(...)` with a path from
 * `endpoints.ts` and get back parsed JSON; everything shared - the base URL,
 * the bearer token, JSON headers, timeouts, turning an error response into a
 * message a screen can show - happens here rather than in each service.
 *
 * Error convention, matching what the screens already branch on:
 *   - the server answered with a non-2xx  -> `ApiError`, carrying its message
 *   - anything else (offline, DNS, CORS,
 *     timeout, abort)                     -> the original error, untouched
 * So `catch (e) { e instanceof ApiError ? e.message : "Network error…" }`
 * keeps working, and only a real server message is ever shown as one.
 */

import { ApiError } from "../http";
import { API_BASE_URL, API_TIMEOUT_MS } from "./config";

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type QueryValue = string | number | boolean | null | undefined;

/** Query string values. `null`/`undefined` entries are dropped, so callers can
 *  pass optional filters straight through without pruning them first. */
export type QueryParams = Record<string, QueryValue>;

export interface RequestOptions {
  /**
   * Bearer token for the call. Clerk issues these per session, and they are
   * short-lived, so callers fetch one with `getToken()` immediately before the
   * request rather than holding on to it.
   */
  token?: string | null;
  query?: QueryParams;
  /** Extra headers, merged over the defaults. */
  headers?: Record<string, string>;
  /** Lets a caller cancel - e.g. an effect aborting on unmount. */
  signal?: AbortSignal;
  /** Overrides `API_TIMEOUT_MS` for a single call (uploads, reports). */
  timeoutMs?: number;
}

/** Message shown when the server said nothing useful about a failure. */
const STATUS_MESSAGES: Record<number, string> = {
  400: "The request could not be processed. Please check the details and try again.",
  401: "Your session has expired. Please sign in again.",
  403: "You do not have access to this.",
  404: "We could not find what you were looking for.",
  409: "That conflicts with something that already exists.",
  422: "Some of the details are not valid. Please review them and try again.",
  429: "Too many requests. Please wait a moment and try again.",
};

const buildUrl = (path: string, query?: QueryParams): string => {
  const url = new URL(`${API_BASE_URL}${path}`);
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, String(value));
    }
  });
  return url.toString();
};

/** Parses the body by content type. Returns `undefined` for an empty body -
 *  a 204, or a 500 that produced no output at all. */
async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;

  const text = await response.text();
  if (!text) return undefined;

  const isJson = (response.headers.get("content-type") ?? "").includes("json");
  if (!isJson) return text;

  try {
    return JSON.parse(text);
  } catch {
    // A JSON content type with a body that is not JSON - a proxy error page,
    // usually. The raw text is more useful to a developer than a parse error.
    return text;
  }
}

/**
 * Digs the human-readable part out of an error body. Covers what DRF sends:
 * `{"detail": "…"}`, `{"message": "…"}`, and field errors like
 * `{"email": ["…"]}`, falling back to a message for the status code.
 */
function errorMessage(body: unknown, status: number): string {
  if (typeof body === "string" && body.trim() && !body.trim().startsWith("<")) {
    return body.trim();
  }

  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;

    for (const key of ["detail", "message", "error"]) {
      const value = record[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }

    // First field error wins: screens show one message, and the first is the
    // one the user most likely needs to fix.
    for (const value of Object.values(record)) {
      if (Array.isArray(value) && typeof value[0] === "string" && value[0]) {
        return value[0];
      }
    }
  }

  return STATUS_MESSAGES[status] ?? `Request failed (${status}).`;
}

/**
 * How a successful response is read. `json` is everything the API answers in
 * words; `blob` is the handful of endpoints that answer with a file — a PDF,
 * for one — where the bytes are the response and parsing them would be
 * nonsense. A failure is read as JSON either way: an endpoint that serves a
 * file still reports why it could not in the usual envelope.
 */
type ResponseKind = "json" | "blob";

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
  kind: ResponseKind = "json"
): Promise<T> {
  const { token, query, headers, signal, timeoutMs = API_TIMEOUT_MS } = options;

  // Our own controller so the timeout can abort the request, chained to the
  // caller's signal so their cancellation still gets through.
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const abortFromCaller = () => controller.abort();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener("abort", abortFromCaller);

  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  try {
    const response = await fetch(buildUrl(path, query), {
      method,
      signal: controller.signal,
      // Every answer here is live account state - a status, a payment, a
      // support ticket - not a document the browser should ever serve a stale
      // copy of from its HTTP cache between requests.
      cache: "no-store",
      headers: {
        Accept: "application/json",
        // Set by the browser for FormData, boundary included - setting it by
        // hand there produces a body the server cannot parse.
        ...(body !== undefined && !isFormData
          ? { "Content-Type": "application/json" }
          : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body:
        body === undefined
          ? undefined
          : isFormData
          ? (body as FormData)
          : JSON.stringify(body),
    });

    if (!response.ok) {
      const payload = await readBody(response);
      throw new ApiError(
        errorMessage(payload, response.status),
        response.status,
        payload
      );
    }

    // Read inside the try, so a connection that drops midway through a large
    // file is still the timeout's to abort.
    if (kind !== "blob") return (await readBody(response)) as T;

    // An endpoint that answers with a file can still answer in words: DRF
    // falls back to its JSON renderer when it has nothing registered for what
    // was asked for, so a 200 carrying an error body is not a document. It is
    // read as the failure it is, message and all, rather than handed on to be
    // saved as a file that opens as nothing.
    if ((response.headers.get("content-type") ?? "").includes("json")) {
      const payload = await readBody(response);
      throw new ApiError(
        errorMessage(payload, response.status),
        response.status,
        payload
      );
    }

    return (await response.blob()) as T;
  } catch (error) {
    // A timeout is the one non-HTTP failure worth naming, because "network
    // error" sends the user to check a connection that is fine.
    if (timedOut) {
      throw new Error("The server took too long to respond.");
    }
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abortFromCaller);
  }
}

/**
 * Verb helpers. `T` is the shape the endpoint returns; services declare it and
 * map the wire response to the app's own types, so snake_case stops at the
 * service boundary.
 */
export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>("GET", path, undefined, options),

  /**
   * The same `GET`, for an endpoint that answers with a file rather than with
   * words — the bytes, undecoded, for the caller to hand to the browser.
   *
   * Callers name what they are asking for with an `Accept` header, since the
   * default one asks for JSON.
   */
  getBlob: (path: string, options?: RequestOptions) =>
    request<Blob>("GET", path, undefined, options, "blob"),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("POST", path, body, options),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PUT", path, body, options),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PATCH", path, body, options),

  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>("DELETE", path, undefined, options),
};
